<template>
  <div>
    <div style="margin-bottom: 20px;">
      <Button
        :label="t('Adicionar Categoria de Serviço')"
        icon="pi pi-plus"
        size="small"
        data-testid="so-service-categories-add-btn"
        @click="handleNew"
      />
    </div>

    <!-- SOServiceCategories Table -->
    <s-o-service-categories-table
      ref="soServiceCategoriesTableRef"
      @edit="handleEdit"
      @delete="handleDelete">
    </s-o-service-categories-table>

    <!-- SOServiceCategoriesModal is centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import SOServiceCategoriesTable from './SOServiceCategoriesTable.vue';
// SOServiceCategoriesModal is centralized in ModalManager.vue
import { log } from '../../utils/DebugLogger';

type Id = number | string;

interface TableInstance {
  loadData: () => void;
}

interface Props {
  translations?: Record<string, unknown>;
}

const props = withDefaults(defineProps<Props>(), {
  translations: () => ({})
});

// Get i18n composable
const { t } = useI18n();

// Reactive data
const soServiceCategoriesTableRef = ref<TableInstance | null>(null);

// Methods
const loadData = async (): Promise<void> => {
  log('SOServiceCategoriesPage', 'loadData', 'Reloading data');
  if (soServiceCategoriesTableRef.value) {
    await soServiceCategoriesTableRef.value.loadData();
  }
};

const handleNew = (): void => {
  log('SOServiceCategoriesPage', 'handleNew', 'Opening modal for new service category via eventBus');
  eventBus.emit('modal.so-service-categories.open', { action: 'create' });
};

const handleEdit = (id?: Id): void => {
  log('SOServiceCategoriesPage', 'handleEdit', 'Editing service category ID via eventBus', id);
  eventBus.emit('modal.so-service-categories.open', { id, action: 'edit' });
};

const handleDelete = (id?: Id, data: Record<string, unknown> | null = null): void => {
  log('SOServiceCategoriesPage', 'handleDelete', 'Deleting service category ID via eventBus', id);
  eventBus.emit('modal.so-service-categories.open', { id, data, action: 'delete' });
};

// Lifecycle
onMounted(() => {
  log('SOServiceCategoriesPage', 'mounted', 'Component mounted');

  // Set up eventBus listeners for backward compatibility
  eventBus.on('so-service-category.new', handleNew);
  eventBus.on('so-service-category.edit', handleEdit);
  eventBus.on('so-service-category.delete', handleDelete);
});

onBeforeUnmount(() => {
  log('SOServiceCategoriesPage', 'beforeUnmount', 'Cleaning up event listeners');

  // Clean up eventBus listeners
  eventBus.off('so-service-category.new', handleNew);
  eventBus.off('so-service-category.edit', handleEdit);
  eventBus.off('so-service-category.delete', handleDelete);
});

// Expose methods for parent components
defineExpose({
  loadData,
  handleNew,
  handleEdit,
  handleDelete
});
</script>

<style scoped>
</style>
